import React, { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import api from "@/lib/api";
import { toast } from "sonner";
import usePushNotifications from "@/hooks/usePushNotifications";
import { 
  Bell,
  BellOff,
  Loader2,
  Truck,
  BookOpen,
  CreditCard,
  Wallet,
  Mail,
  Smartphone,
  CheckCircle
} from "lucide-react";

export default function NotificationSettings() {
  const [preferences, setPreferences] = useState({
    delivery_status: true,
    new_bookings: true,
    payment_confirmations: true,
    withdrawal_status: true,
    push_enabled: true,
    email_enabled: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [subscribing, setSubscribing] = useState(false);

  // Use push notifications hook
  const {
    isSupported: pushSupported,
    isSubscribed,
    permission: pushPermission,
    loading: pushLoading,
    subscribe,
    unsubscribe
  } = usePushNotifications();

  const fetchPreferences = useCallback(async () => {
    try {
      const response = await api.get("/push/preferences");
      setPreferences(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn("Failed to fetch preferences:", error.message);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPreferences();
  }, [fetchPreferences]);

  const handleToggle = async (key) => {
    const newPreferences = {
      ...preferences,
      [key]: !preferences[key]
    };
    setPreferences(newPreferences);
    
    setSaving(true);
    try {
      await api.put("/push/preferences", newPreferences);
      toast.success("Preferences updated");
    } catch (error) {
      setPreferences(preferences);
      toast.error("Failed to update preferences");
    } finally {
      setSaving(false);
    }
  };

  const handleEnablePush = async () => {
    if (!pushSupported) {
      toast.error("Push notifications not supported in this browser");
      return;
    }

    setSubscribing(true);
    try {
      await subscribe();
      toast.success("Push notifications enabled! You'll now receive real-time updates.");
    } catch (error) {
      toast.error(error.message || "Failed to enable push notifications");
    } finally {
      setSubscribing(false);
    }
  };

  const handleDisablePush = async () => {
    setSubscribing(true);
    try {
      await unsubscribe();
      toast.success("Push notifications disabled");
    } catch (error) {
      toast.error("Failed to disable push notifications");
    } finally {
      setSubscribing(false);
    }
  };

  if (loading || pushLoading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  const notificationTypes = [
    {
      key: "delivery_status",
      label: "Delivery Status Updates",
      description: "Get notified when delivery status changes (picked up, in transit, delivered)",
      icon: Truck
    },
    {
      key: "new_bookings",
      label: "New Bookings",
      description: "Get notified when someone books your cargo space or jet seats",
      icon: BookOpen
    },
    {
      key: "payment_confirmations",
      label: "Payment Confirmations",
      description: "Get notified when payments are received or processed",
      icon: CreditCard
    },
    {
      key: "withdrawal_status",
      label: "Withdrawal Status",
      description: "Get notified about your withdrawal request status",
      icon: Wallet
    }
  ];

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-black tracking-tight mb-2">Notification Settings</h1>
        <p className="text-muted-foreground">Manage how you receive notifications</p>
      </div>

      {/* Push Notification Permission */}
      <div className="border border-border p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 flex items-center justify-center ${
              isSubscribed ? 'bg-green-500' : 'bg-primary'
            }`}>
              {isSubscribed ? (
                <CheckCircle className="w-5 h-5 text-white" />
              ) : (
                <Bell className="w-5 h-5 text-primary-foreground" />
              )}
            </div>
            <div>
              <h3 className="font-bold">Browser Push Notifications</h3>
              <p className="text-sm text-muted-foreground">
                {isSubscribed 
                  ? "Active - You'll receive real-time notifications"
                  : pushPermission === "denied"
                    ? "Blocked - Enable in browser settings"
                    : "Not enabled - Click to enable instant notifications"}
              </p>
            </div>
          </div>
          
          {!isSubscribed && pushPermission !== "denied" && (
            <Button 
              onClick={handleEnablePush}
              className="rounded-none"
              disabled={!pushSupported || subscribing}
              data-testid="enable-push-btn"
            >
              {subscribing ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Bell className="w-4 h-4 mr-2" />
              )}
              Enable
            </Button>
          )}
          
          {pushPermission === "denied" && !isSubscribed && (
            <Button 
              variant="outline"
              className="rounded-none"
              disabled
              data-testid="enable-push-btn"
            >
              <BellOff className="w-4 h-4 mr-2" />
              Blocked
            </Button>
          )}
          
          {isSubscribed && (
            <div className="flex items-center gap-2">
              <span className="px-3 py-1 text-xs font-bold uppercase bg-green-100 text-green-800">
                Active
              </span>
              <Button 
                variant="ghost"
                size="sm"
                onClick={handleDisablePush}
                disabled={subscribing}
                className="text-muted-foreground hover:text-destructive"
              >
                Disable
              </Button>
            </div>
          )}
        </div>
        
        {isSubscribed && (
          <p className="text-xs text-green-600 bg-green-50 p-2 border border-green-200">
            You're all set! Push notifications are active for this browser.
          </p>
        )}
      </div>

      {/* Notification Channels */}
      <div className="border border-border divide-y divide-border">
        <div className="p-4 bg-muted/30">
          <h3 className="font-bold">Notification Channels</h3>
        </div>
        
        <div className="p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Smartphone className="w-5 h-5 text-muted-foreground" />
            <div>
              <p className="font-medium">Push Notifications</p>
              <p className="text-sm text-muted-foreground">Receive notifications in your browser</p>
            </div>
          </div>
          <Switch
            checked={preferences.push_enabled}
            onCheckedChange={() => handleToggle("push_enabled")}
            disabled={saving}
            data-testid="push-toggle"
          />
        </div>
        
        <div className="p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Mail className="w-5 h-5 text-muted-foreground" />
            <div>
              <p className="font-medium">Email Notifications</p>
              <p className="text-sm text-muted-foreground">Receive notifications via email</p>
            </div>
          </div>
          <Switch
            checked={preferences.email_enabled}
            onCheckedChange={() => handleToggle("email_enabled")}
            disabled={saving}
            data-testid="email-toggle"
          />
        </div>
      </div>

      {/* Notification Types */}
      <div className="border border-border divide-y divide-border">
        <div className="p-4 bg-muted/30">
          <h3 className="font-bold">Notification Types</h3>
        </div>
        
        {notificationTypes.map(({ key, label, description, icon: Icon }) => (
          <div key={key} className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Icon className="w-5 h-5 text-muted-foreground" />
              <div>
                <p className="font-medium">{label}</p>
                <p className="text-sm text-muted-foreground">{description}</p>
              </div>
            </div>
            <Switch
              checked={preferences[key]}
              onCheckedChange={() => handleToggle(key)}
              disabled={saving}
              data-testid={`${key}-toggle`}
            />
          </div>
        ))}
      </div>

      {saving && (
        <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" />
          Saving...
        </div>
      )}
    </div>
  );
}
